import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CalendarDays, ClipboardList, MapPin } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { Card, CardContent } from '../ui/card'
import { DashboardSection } from './DashboardSection'
import StageEntryDialogs from '../trophy/StageEntryDialogs'
import StageEntryAction from '../trophy/StageEntryAction'
import { useStageEntry, withStageStanding } from '../trophy/useStageEntry'
import { useAuth } from '../../hooks/useAuth'
import { trophyBadge } from '../../utils/lifecycle-status'
import type { DashboardTrophy } from '../../types/dashboard'
import type { StageParticipation } from '../../types/trophy'

function formatDate(value?: string | null) {
  if (!value) return null
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

interface TrophiesSectionProps {
  trophies: DashboardTrophy[]
}

/**
 * The circuits the climber is entered in, each laid out stage by stage.
 *
 * Being entered in one stage is what puts a circuit here, and the point of
 * showing the rest is the next weekend: the stage they are not in yet gets a
 * button, the ones they are in get a tick. Nobody entered in anything sees no
 * section at all — an empty "your trophies" is noise on a homepage — and nor
 * does a season whose last stage is over: the API sends only circuits with a
 * stage still to come.
 *
 * A parent sees their children's circuits here too, one card each, named: the
 * child's season is what the parent came to the homepage for, and two children
 * in the same circuit are two cards rather than one ambiguous row.
 *
 * A **team manager** sees the circuits they work at on the same footing, with the
 * stages that are theirs to mark flagged — being named on a squad is reason enough
 * for a season to be on your homepage, entered in it or not, since the job is on
 * the stage's round page and they have to be able to reach it.
 */
export function TrophiesSection({ trophies }: TrophiesSectionProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [circuits, setCircuits] = useState(trophies)

  // Only the card the button was on: a parent and a child can both be in the
  // same circuit, which is two cards holding the same stage id.
  const onEntered = useCallback(
    (standing: StageParticipation, athlete?: { user_id: string } | null) =>
      setCircuits((current) =>
        current.map((circuit) => {
          const sameAthlete = athlete
            ? circuit.athlete?.user_id === athlete.user_id
            : !circuit.athlete || circuit.athlete.self
          const hasStage = circuit.stages.some((stage) => stage.id === standing.id)
          return sameAthlete && hasStage ? withStageStanding(circuit, standing) : circuit
        })
      ),
    []
  )
  const entry = useStageEntry(onEntered)
  const pendingCircuit = entry.pending
    ? circuits.find((circuit) => circuit.stages.some((stage) => stage.id === entry.pending?.id))
    : null

  if (circuits.length === 0) return null

  return (
    <DashboardSection title={t('dashboard.trophies.title')}>
      <div className="flex flex-col gap-3">
        {circuits.map((circuit) => (
          <Card key={circuit.id} data-testid="dashboard-trophy">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-3">
                <Link to={`/trophies/${circuit.slug}`} className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar className="size-10 border">
                    <AvatarImage src={circuit.logo_small_url ?? undefined} alt={circuit.name} loading="lazy" />
                    <AvatarFallback className="text-xs">{circuit.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="min-w-0">
                    <span className="block truncate font-semibold">{circuit.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {circuit.athlete && !circuit.athlete.self && (
                        <span className="font-medium text-foreground" data-testid="trophy-athlete">
                          {circuit.athlete.full_name}
                          {' · '}
                        </span>
                      )}
                      {t('stageEntry.summaryEntered', {
                        count: circuit.entries_count,
                        total: circuit.stages.length,
                      })}
                    </span>
                  </span>
                </Link>
                {/* Why the circuit is here at all, for somebody who works at it
                    without climbing in it. */}
                {circuit.team_manager && (
                  <Badge variant="outline" data-testid="trophy-team-manager">
                    {t('dashboard.trophies.teamManager')}
                  </Badge>
                )}
                <Badge variant={trophyBadge(circuit.status).variant}>
                  {t(trophyBadge(circuit.status).labelKey)}
                </Badge>
              </div>

              <ul className="divide-y">
                {circuit.stages.map((stage) => (
                  <li
                    key={stage.id}
                    className="flex items-center justify-between gap-3 py-2"
                    data-testid="dashboard-stage"
                  >
                    <Link to={`/competitions/${stage.slug}`} className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{stage.name}</span>
                      <span className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                        {stage.start_date && (
                          <span className="flex items-center gap-1">
                            <CalendarDays className="size-3" aria-hidden />
                            {formatDate(stage.start_date)}
                          </span>
                        )}
                        {(stage.venue_name || stage.city) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="size-3" aria-hidden />
                            {stage.venue_name ?? stage.city}
                          </span>
                        )}
                        {/* Which weekends are theirs to mark: an appointment is
                            per stage, so a manager at one is not one at all of
                            them. */}
                        {stage.managed && (
                          <span
                            className="flex items-center gap-1 font-medium text-foreground"
                            data-testid="stage-team-manager"
                          >
                            <ClipboardList className="size-3" aria-hidden />
                            {t('dashboard.trophies.teamManager')}
                          </span>
                        )}
                      </span>
                    </Link>
                    {/* Always named, self included: every card here is about
                        one athlete already, so there is nothing for the
                        picker to ask (see useStageEntry). */}
                    <StageEntryAction
                      stage={stage}
                      signedIn={!!user}
                      athlete={
                        circuit.athlete
                          ? { user_id: circuit.athlete.user_id, full_name: circuit.athlete.full_name }
                          : null
                      }
                      onRequestEnter={entry.requestEnter}
                    />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <StageEntryDialogs entry={entry} trophyName={pendingCircuit?.name ?? ''} />
    </DashboardSection>
  )
}
